<template>
  <ClientOnly>
    <n-config-provider :locale="zhCN" :date-locale="dateZhCN">
      <n-message-provider>
        <n-dialog-provider>
          <div class="app-container">
          <!-- 左侧边栏 -->
          <Sidebar v-if="isAuthenticated" />
          
          <!-- 右侧主内容区域 -->
          <div class="main-wrapper" :style="{ marginLeft: isAuthenticated ? (sidebarCollapsed ? '64px' : '240px') : '0' }">
            <!-- 管理员代登提示条（组织代登状态展示在 NavBar 中间） -->
            <div v-if="authStore.impersonating" class="impersonation-bar">
              <span>
                当前以 <strong>{{ authStore.user?.real_name || authStore.user?.username }}</strong> 身份查看
                <template v-if="authStore.adminUser">
                  （管理员：{{ authStore.adminUser.real_name || authStore.adminUser.username }}）
                </template>
              </span>
              <n-button size="small" type="warning" ghost :loading="switchingBack" @click="handleSwitchBack">
                切回管理员
              </n-button>
            </div>
            <!-- 顶部导航栏 -->
            <NavBar />
            
            <!-- 标签页栏 -->
            <div v-if="isAuthenticated && tabs.length > 0" class="tabs-bar">
              <n-tabs
                v-model:value="activeTabKey"
                type="card"
                closable
                @close="handleTabClose"
                @update:value="handleTabChange"
              >
                <n-tab-pane
                  v-for="tab in tabs"
                  :key="tab.key"
                  :name="tab.key"
                  :tab="tab.title"
                  :closable="tab.closable !== false"
                >
                </n-tab-pane>
              </n-tabs>
            </div>
            
            <!-- 主要内容区域（可滚动） -->
            <div class="content-wrapper">
              <slot />
            </div>
          </div>
        </div>
      </n-dialog-provider>
    </n-message-provider>
    </n-config-provider>
    
    <template #fallback>
      <div class="app-loading">
        <n-spin size="large" />
      </div>
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, ref, provide, watch, onMounted, nextTick } from 'vue'
import { NMessageProvider, NDialogProvider, NSpin, NTabs, NTabPane, NConfigProvider, NButton, zhCN, dateZhCN } from 'naive-ui'
import Sidebar from '../components/Sidebar.vue'
import NavBar from '../components/NavBar.vue'

const authStore = useAuthStore()
const { switchBack } = useAuth()
const route = useRoute()
const router = useRouter()

const switchingBack = ref(false)
async function handleSwitchBack() {
  switchingBack.value = true
  try {
    await switchBack()
  } finally {
    switchingBack.value = false
  }
}

// 侧边栏折叠状态
const sidebarCollapsed = ref(false)
provide('sidebarCollapsed', sidebarCollapsed)

// 标签页管理
interface TabItem {
  key: string
  title: string
  path: string
  closable?: boolean
}

const tabs = ref<TabItem[]>([])
const activeTabKey = ref('')
/** 程序内改 activeTabKey 时置位，避免再触发 handleTabChange 二次跳转 */
const syncingTabFromCode = ref(false)

const setActiveTabKey = (key: string) => {
  if (activeTabKey.value === key) return
  syncingTabFromCode.value = true
  activeTabKey.value = key
  nextTick(() => {
    nextTick(() => {
      syncingTabFromCode.value = false
    })
  })
}

const normalizeTabPath = (path: string) => {
  // 统一成可比较的绝对路径（去尾斜杠，保留 query）
  if (!path) return path
  if (path.startsWith('/')) return path
  return `/${path}`
}

// 添加标签页
const addTab = (tab: TabItem, skipNavigation = false) => {
  const nextPath = normalizeTabPath(tab.path)
  const existingTab = tabs.value.find(t => t.key === tab.key)
  if (existingTab) {
    existingTab.path = nextPath
    if (tab.title) existingTab.title = tab.title
  } else {
    tabs.value.push({ ...tab, path: nextPath })
  }

  setActiveTabKey(tab.key)

  if (!skipNavigation && route.fullPath !== nextPath) {
    router.push(nextPath).catch(() => {})
  }
}

// 移除标签页
const removeTab = (key: string) => {
  const index = tabs.value.findIndex(t => t.key === key)
  if (index === -1) return
  
  tabs.value.splice(index, 1)
  
  if (activeTabKey.value === key) {
    if (tabs.value.length > 0) {
      const newActiveTab = tabs.value[Math.min(index, tabs.value.length - 1)]
      if (newActiveTab) {
        setActiveTabKey(newActiveTab.key)
        router.push(newActiveTab.path).catch(() => {})
      }
    } else {
      setActiveTabKey('')
      router.push('/').catch(() => {})
    }
  }
}

// 标签页关闭
const handleTabClose = (key: string) => {
  removeTab(key)
}

// 标签页切换（仅用户点击 tab 时导航；程序改 key 会跳过）
const handleTabChange = (key: string) => {
  if (syncingTabFromCode.value) return
  const tab = tabs.value.find(t => t.key === key)
  if (!tab) return
  console.log('[default.vue] handleTabChange:', key, 'current path:', route.fullPath, 'tab path:', tab.path)
  if (route.fullPath !== tab.path) {
    router.push(tab.path).catch(() => {})
  }
}

const isAuthenticated = computed(() => {
  return authStore.user && authStore.isUserAuthenticated
})

// 防止重复添加标签的标志
const isAddingTab = ref(false)

// 处理路由变化的函数
const handleRouteChange = (newPath: string) => {
  console.log('[default.vue] handleRouteChange:', newPath, 'isAddingTab:', isAddingTab.value)
  if (isAddingTab.value) return
  
  // 登录页不添加标签
  if (newPath === '/auth/login') {
    return
  }
  
  isAddingTab.value = true
  
  try {
    // 首页
    if (newPath === '/') {
      const tabKey = 'home'
      addTab({
        key: tabKey,
        title: '首页',
        path: '/',
        closable: false
      }, true)
    } else if (newPath.startsWith('/advertisers') && newPath !== '/advertisers') {
      // 店铺详情页，从路由中提取店铺ID
      const match = newPath.match(/\/advertisers\/(\d+)/)
      if (match) {
        const advertiserId = match[1]
        const tabKey = `advertiser-${advertiserId}`
        const existingTab = tabs.value.find(t => t.key === tabKey)
        if (!existingTab) {
          addTab({
            key: tabKey,
            title: `店铺 ${advertiserId}`,
            path: newPath,
            closable: true
          })
        } else {
          setActiveTabKey(tabKey)
        }
      }
    } else if (newPath === '/advertisers') {
      const tabKey = 'advertisers'
      // 使用 skipNavigation 参数，避免路由跳转
      addTab({
        key: tabKey,
        title: '店铺管理',
        path: '/advertisers',
        closable: true
      }, true) // skipNavigation = true，避免路由跳转
    } else if (newPath === '/auth/qianchuan') {
      const tabKey = 'qianchuan'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '千川授权',
          path: '/auth/qianchuan',
          closable: true
        })
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath === '/org-grants' || newPath.startsWith('/org-grants')) {
      const tabKey = 'org-grants'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '服务授权',
          path: '/org-grants',
          closable: true
        })
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath === '/org-operation-logs' || newPath.startsWith('/org-operation-logs')) {
      const tabKey = 'org-operation-logs'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '操作日志',
          path: '/org-operation-logs',
          closable: true
        })
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/rpa/tokens')) {
      const tabKey = 'rpa-tokens'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: 'RPA 配置',
          path: '/rpa/tokens',
          closable: true
        })
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/modeling')) {
      // 建模数据页面，从查询参数中提取店铺ID
      const advertiserId = route.query.advertiser_id as string
      if (advertiserId) {
        const tabKey = `modeling-${advertiserId}`
        const existingTab = tabs.value.find(t => t.key === tabKey)
        const tabPath = `/modeling?advertiser_id=${advertiserId}`
        if (!existingTab) {
          addTab({
            key: tabKey,
            title: `店铺 ${advertiserId} - 全部计划`,
            path: tabPath,
            closable: true
          }, true)
        } else {
          // 纠正历史错误：path 丢了 query 会导致切不回去
          existingTab.path = tabPath
          setActiveTabKey(tabKey)
        }
      } else {
        // 没有指定店铺ID，使用通用标签
        const tabKey = 'modeling'
        const existingTab = tabs.value.find(t => t.key === tabKey)
        if (!existingTab) {
          addTab({
            key: tabKey,
            title: '账号建模',
            path: '/modeling',
            closable: true
          }, true)
        } else {
          setActiveTabKey(tabKey)
        }
      }
    } else if (newPath.startsWith('/transfer-shop')) {
      const tabKey = 'transfer-shop'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '转投店铺',
          path: '/transfer-shop',
          closable: true
        })
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/algorithm-tasks')) {
      const tabKey = 'algorithm-tasks'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '算法任务',
          path: '/algorithm-tasks',
          closable: true
        })
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath === '/products') {
      const tabKey = 'products'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '商品管理',
          path: '/products',
          closable: true
        }, true) // skipNavigation = true，避免路由跳转
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/auto-placement')) {
      const tabKey = 'auto-placement'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '自动投放',
          path: '/auto-placement',
          closable: true
        }, true)
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/budget-settings')) {
      const tabKey = 'budget-settings'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '预算管理',
          path: '/budget-settings',
          closable: true
        }, true)
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/invalid-campaign-management')) {
      const tabKey = 'invalid-campaign-management'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '无效计划',
          path: '/invalid-campaign-management',
          closable: true
        }, true)
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/ai-video-prompts')) {
      if (!authStore.hasAdminAccess) {
        router.replace('/')
        return
      }
      const tabKey = 'ai-video-prompts'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: 'AI视频提示词',
          path: '/ai-video-prompts',
          closable: true
        }, true)
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/account-email')) {
      const tabKey = 'account-email'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: '账号邮箱',
          path: '/account-email',
          closable: true
        }, true)
      } else {
        setActiveTabKey(tabKey)
      }
    } else if (newPath.startsWith('/ai-paid-video')) {
      if (!authStore.hasAdminAccess) {
        router.replace('/')
        return
      }
      const tabKey = 'ai-paid-video'
      const existingTab = tabs.value.find(t => t.key === tabKey)
      if (!existingTab) {
        addTab({
          key: tabKey,
          title: 'AI付费视频',
          path: '/ai-paid-video',
          closable: true
        }, true)
      } else {
        setActiveTabKey(tabKey)
      }
    }
  } finally {
    // 延迟重置标志，确保操作完成
    nextTick(() => {
      isAddingTab.value = false
    })
  }
}

// 监听路由变化，自动添加标签
watch(() => route.path, (newPath, oldPath) => {
  if (!isAuthenticated.value) return
  if (newPath === oldPath) return
  if (isAddingTab.value) return
  
  handleRouteChange(newPath)
}, { immediate: false })

// 全部计划同 path 不同店铺：path 不变，需按 query 同步激活标签
watch(
  () => [route.path, route.query.advertiser_id] as const,
  ([path, advertiserId]) => {
    if (!isAuthenticated.value) return
    if (path !== '/modeling' || advertiserId == null || advertiserId === '') return
    const tabKey = `modeling-${advertiserId}`
    const tab = tabs.value.find(t => t.key === tabKey)
    if (tab) {
      tab.path = `/modeling?advertiser_id=${advertiserId}`
      if (activeTabKey.value !== tabKey) {
        setActiveTabKey(tabKey)
      }
    }
  }
)

// 提供获取当前激活标签 key 的方法
const getActiveTabKey = () => {
  return activeTabKey.value
}

// 提供标签页操作给子组件
provide('addTab', addTab)
provide('removeTab', removeTab)
provide('getActiveTabKey', getActiveTabKey)
provide('tabs', tabs)

onMounted(async () => {
  if (!authStore.user) {
    await authStore.fetchUserInfo()
  }

  // 在组件挂载后，初始化当前路由的标签
  // 延迟执行，确保路由已经稳定
  nextTick(() => {
    if (route.path && route.path !== '/' && route.path !== '/auth/login') {
      handleRouteChange(route.path)
    }
  })
})
</script>

<style scoped>
.app-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
}

.main-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100vh;
  transition: margin-left 0.3s;
  overflow: hidden;
}

.tabs-bar {
  padding: 0 20px;
  border-bottom: 1px solid #e0e0e6;
  background: #fff;
  flex-shrink: 0;
}

.content-wrapper {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f5f7fa;
  padding: 20px;
  box-sizing: border-box;
}

/* 允许子页收缩，表格 scroll-x 才能出现横向滚动，而不是被裁切 */
.content-wrapper > * {
  min-width: 0;
  max-width: 100%;
}

/* 预算管理等「表格占满」页：内容区不外滚，由页面内部 flex 自适应 */
.content-wrapper:has(.budget-settings-page) {
  overflow: hidden;
}
.content-wrapper:has(.budget-settings-page) > * {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 账号建模：整页纵向滚动，宽度限制在视口内；表头 sticky 贴齐标签栏 */
.content-wrapper:has(.modeling-page) {
  padding-top: 0;
}

.impersonation-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 6px 20px;
  background: #fff3e0;
  border-bottom: 1px solid #ffcc80;
  color: #e65100;
  font-size: 13px;
  flex-shrink: 0;
}

.app-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f5f7fa;
}

:deep(.n-tabs .n-tabs-nav) {
  padding: 0;
}

:deep(.n-tabs .n-tabs-tab) {
  padding: 8px 16px;
}
</style>
